import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import {
  DUCK_USE_IMAGES,
  DuckColorDef,
  duckColor,
  duckImageUrl,
} from '../../../core/models/duck-race.model';

/** Đếm số con vịt đã tạo — dùng sinh id gradient duy nhất. */
let duckInstanceSeq = 0;

/**
 * Vịt cao su vẽ bằng SVG — không cần file ảnh, đổi màu tức thì, nét ở mọi
 * độ phân giải. Dùng chung cho trang chơi và màn hình điều khiển.
 *
 * Bốn đặc điểm làm nên dáng "vịt cao su bóng" thay vì hình vẽ phẳng:
 *   1. Mắt to có TRÒNG TRẮNG bao quanh con ngươi (không phải chấm đen).
 *   2. Mỏ HÉ MỞ, thấy khoang miệng đỏ bên trong.
 *   3. CHỎM TÓC nhỏ trên đỉnh đầu.
 *   4. Vệt BÓNG LOÁNG mềm trên đầu và lưng — đây là thứ tạo cảm giác nhựa bóng.
 *
 * Khối lượng chi tiết cố ý dồn hết vào SVG chứ không vào SCSS: SVG nằm trong
 * template nên không tính vào ngưỡng `anyComponentStyle` 6 kB của angular.json.
 *
 * ⚠️ Id của gradient trong SVG là TOÀN CỤC theo trang. Trang đua có hàng chục
 * con vịt khác màu cùng lúc, nên mỗi instance phải có hậu tố id riêng — nếu
 * dùng id cố định thì mọi con sẽ ăn gradient của con render sau cùng.
 *
 * Dùng gradient chứ không dùng `<filter>` để làm mềm vệt bóng: filter SVG tính
 * lại theo từng khung hình, với vài chục con vịt đang chạy là tụt hẳn khung hình
 * trên điện thoại tầm trung.
 */
@Component({
  selector: 'app-duck-sprite',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./duck-sprite.component.scss'],
  template: `
    <!-- Chế độ ảnh: bật DUCK_USE_IMAGES sau khi đã bỏ đủ file vào assets/duck/ -->
    <img
      *ngIf="useImages; else vector"
      class="duck"
      [class.duck--buffed]="buffed"
      [style.width.px]="size"
      [src]="imgSrc"
      alt=""
    />

    <ng-template #vector>
    <svg
      class="duck"
      [class.duck--buffed]="buffed"
      [style.width.px]="size"
      viewBox="0 0 104 92"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <radialGradient [attr.id]="'db' + uid" cx="30%" cy="20%" r="88%">
          <stop offset="0%" [attr.stop-color]="c.belly" />
          <stop offset="46%" [attr.stop-color]="c.body" />
          <stop offset="100%" [attr.stop-color]="c.dark" />
        </radialGradient>

        <radialGradient [attr.id]="'dh' + uid" cx="28%" cy="18%" r="92%">
          <stop offset="0%" stop-color="#ffffff" stop-opacity="0.75" />
          <stop offset="26%" [attr.stop-color]="c.belly" />
          <stop offset="64%" [attr.stop-color]="c.body" />
          <stop offset="100%" [attr.stop-color]="c.dark" />
        </radialGradient>

        <!-- Vệt bóng: trắng đặc ở giữa, tan dần ra mép -->
        <radialGradient [attr.id]="'dg' + uid" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#ffffff" stop-opacity="0.9" />
          <stop offset="60%" stop-color="#ffffff" stop-opacity="0.4" />
          <stop offset="100%" stop-color="#ffffff" stop-opacity="0" />
        </radialGradient>

        <linearGradient [attr.id]="'dk' + uid" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#ffffff" stop-opacity="0.55" />
          <stop offset="35%" [attr.stop-color]="c.beak" />
          <stop offset="100%" [attr.stop-color]="c.beak" />
        </linearGradient>
      </defs>

      <!-- Gợn nước -->
      <ellipse cx="46" cy="84" rx="42" ry="7" fill="#ffffff" opacity="0.26" />
      <ellipse cx="44" cy="83" rx="29" ry="4.5" fill="#ffffff" opacity="0.4" />

      <!-- Đuôi -->
      <path
        d="M24 45 C16 44 9 39 6 31 C14 31 21 36 27 43 Z"
        [attr.fill]="c.body"
        [attr.stroke]="c.dark"
        stroke-width="2"
        stroke-linejoin="round"
      />

      <!-- Chỏm tóc, vẽ trước đầu để chân chỏm bị đầu che -->
      <path
        d="M60 14 C58 3 66 -2 70 2 C74 7 71 14 67 15 Z"
        [attr.fill]="c.body"
        [attr.stroke]="c.dark"
        stroke-width="2"
        stroke-linejoin="round"
      />

      <!-- Thân -->
      <ellipse
        cx="44"
        cy="57"
        rx="38"
        ry="28"
        [attr.fill]="'url(#db' + uid + ')'"
        [attr.stroke]="c.dark"
        stroke-width="2.1"
      />

      <!-- Cánh: khối nổi trên sườn -->
      <path
        d="M28 60 C28 50 36 44 47 47 C50 57 43 67 34 67 C30 67 28 64 28 60 Z"
        [attr.fill]="c.belly"
        [attr.stroke]="c.dark"
        stroke-width="1.6"
        stroke-linejoin="round"
        opacity="0.5"
      />
      <path
        d="M34 62 C36 55 41 51 46 50"
        fill="none"
        [attr.stroke]="c.dark"
        stroke-width="1.4"
        stroke-linecap="round"
        opacity="0.35"
      />

      <!-- Đầu -->
      <circle
        cx="66"
        cy="29"
        r="23"
        [attr.fill]="'url(#dh' + uid + ')'"
        [attr.stroke]="c.dark"
        stroke-width="2.1"
      />

      <!-- Mỏ trên -->
      <path
        d="M84 23 C95 21 103 25 103 30 C97 32 88 32 84 31 Z"
        [attr.fill]="'url(#dk' + uid + ')'"
        [attr.stroke]="c.dark"
        stroke-width="1.5"
        stroke-linejoin="round"
      />
      <!-- Khoang miệng -->
      <path d="M86 30 C93 32 100 31 103 30 C99 37 89 38 86 34 Z" fill="#B03A48" />
      <!-- Mỏ dưới -->
      <path
        d="M86 33 C93 36 99 35 101 33 C98 40 88 41 86 37 Z"
        [attr.fill]="c.beak"
        [attr.stroke]="c.dark"
        stroke-width="1.4"
        stroke-linejoin="round"
      />

      <!-- Má ửng -->
      <ellipse cx="61" cy="39" rx="6" ry="3.6" fill="#FF7BA9" opacity="0.32" />

      <!-- Mắt: tròng trắng + con ngươi to + đốm sáng -->
      <ellipse cx="74" cy="25" rx="7.6" ry="8.6" fill="#ffffff" />
      <ellipse cx="74" cy="25" rx="7.6" ry="8.6" fill="none" stroke="#2C2F36" stroke-width="0.9" opacity="0.35" />
      <ellipse cx="75.4" cy="26" rx="4.7" ry="5.5" fill="#241F26" />
      <circle cx="77.2" cy="22.8" r="2" fill="#ffffff" />
      <circle cx="72.9" cy="28.6" r="1.1" fill="#ffffff" opacity="0.75" />

      <!-- Bóng loáng trên đầu và lưng -->
      <ellipse
        cx="56"
        cy="16"
        rx="11"
        ry="6.5"
        [attr.fill]="'url(#dg' + uid + ')'"
        transform="rotate(-26 56 16)"
      />
      <ellipse
        cx="30"
        cy="42"
        rx="16"
        ry="7"
        [attr.fill]="'url(#dg' + uid + ')'"
        transform="rotate(-16 30 42)"
        opacity="0.75"
      />

      <!-- Bọt nước rẽ sóng -->
      <path d="M62 80 C72 72 82 76 88 79 C79 87 66 86 62 82 Z" fill="#ffffff" opacity="0.85" />
      <circle cx="16" cy="78" r="3" fill="#ffffff" opacity="0.6" />

      <!--
        Khi được buff chỉ thêm một vệt sáng rất mờ trên lưng vịt. Trước đây chỗ
        này là vòng hào quang vàng to đùng — nhìn phát là biết ngay con nào được
        tăng tốc, mất hết bất ngờ của cuộc đua.
      -->
      <ellipse
        *ngIf="buffed"
        cx="52"
        cy="44"
        rx="26"
        ry="20"
        fill="#ffffff"
        class="duck__aura"
      />
    </svg>
    </ng-template>
  `,
})
export class DuckSpriteComponent {
  /** Khoá màu trong DUCK_COLORS. */
  @Input() set color(value: string) {
    this.c = duckColor(value);
    this.imgSrc = duckImageUrl(this.c.key);
  }

  /** Chiều rộng con vịt tính bằng px. */
  @Input() size = 56;

  /**
   * Con vịt đang được buff ở những giây cuối. Chỉ đổi ánh sáng rất nhẹ — người
   * chơi không nên nhìn ra ngay con nào được ưu ái.
   */
  @Input() buffed = false;

  c: DuckColorDef = duckColor('yellow');

  /** Đường dẫn ảnh tương ứng màu hiện tại (chỉ dùng khi bật chế độ ảnh). */
  imgSrc = duckImageUrl('yellow');

  readonly useImages = DUCK_USE_IMAGES;

  /** Hậu tố id riêng cho instance này. */
  readonly uid = ++duckInstanceSeq;
}
